/**
 * Synthesize brand / category / series section rows from matched parts (e.g. a
 * part-number search still shows brand, category, and series). Derived rows are
 * always built and merged with whatever the secondary index returned, and they
 * take precedence: derived rows lead, the index's own hits fill the remaining
 * slots up to the section's hitsPerPage, and duplicates are dropped. Where
 * counts exist (categories) only the first derived row is pinned and the rest
 * of the pool competes on count, so parts spanning many subcategories cannot
 * spend the whole display budget on low-count rows.
 *
 * The parts index is the most literal answer to a query like "23-0500" — the
 * categories/series indexes only fuzzy-match the digits ("Z-23 Shields",
 * "1-2-3 Series Radiators"), so the sections led with noise before this.
 *
 * The same parts response also carries a facet distribution, which is where
 * category row counts come from. Counts cover every part matching the query,
 * not just the handful of hits the parts section returned, so they are the one
 * number here that is not limited by the parts section's hitsPerPage.
 */

/**
 * Facets to request on the parts query. The categories index groups by
 * SubCategory — `name` on a category document IS the subcategory — so
 * `subcategory` is the field whose facet values line up with those rows.
 *
 * Only request what is consumed below; an unused facet is work Meilisearch
 * does on every keystroke for nothing.
 */
export var AUTOCOMPLETE_PARTS_FACETS = ['subcategory'];

/** The facet whose counts get attached to category rows. */
var PARTS_COUNT_FACET = 'subcategory';

export function slugifyAutocompleteValue(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function getPartStringField(hit, field) {
    if (!hit) {
        return undefined;
    }

    var candidates = [hit[field]];
    var pascal =
        field.charAt(0).toUpperCase() + field.slice(1);
    if (pascal !== field) {
        candidates.push(hit[pascal]);
    }

    // Common PACE / legacy aliases.
    if (field === 'brand') {
        if (hit.brand && typeof hit.brand === 'object') {
            candidates.push(hit.brand.name, hit.brand.label);
        }
        candidates.push(
            hit.BrandLabel,
            hit.BrandName,
            hit.brand_label,
            hit.Brand
        );
    }
    if (field === 'category') {
        candidates.push(hit.Category);
    }
    if (field === 'subcategory') {
        candidates.push(hit.SubCategory);
    }
    if (field === 'series') {
        candidates.push(hit.Series);
    }
    if (field === 'aaia_brand_code') {
        candidates.push(hit.AAIABrandCode, hit.aaiaBrandCode);
    }

    for (var i = 0; i < candidates.length; i++) {
        var value = candidates[i];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return undefined;
}

function extractBrandSlugFromPart(hit) {
    var explicit =
        (hit &&
            (hit.brand_slug ||
                hit.brandSlug ||
                hit.brand_url_string)) ||
        '';
    if (explicit && String(explicit).trim()) {
        return String(explicit).trim();
    }

    var partSlug = (hit && hit.slug ? String(hit.slug) : '').trim();
    var match = partSlug.match(/^catalog\/item\/([^/]+)\//i);
    if (match && match[1]) {
        return match[1];
    }

    var brandName = getPartStringField(hit, 'brand');
    return brandName ? slugifyAutocompleteValue(brandName) : '';
}

function getBrandImageFromPart(hit) {
    var directImage =
        getPartStringField(hit, 'brand_image') ||
        getPartStringField(hit, 'brand_logo') ||
        getPartStringField(hit, 'manufacturer_image');

    if (directImage) {
        return directImage;
    }

    var manufacturerData = hit && hit.manufacturer_data;
    if (
        manufacturerData &&
        typeof manufacturerData === 'object' &&
        !manufacturerData.hide_brand_logo
    ) {
        var fromManufacturer =
            manufacturerData.aam_image || manufacturerData.image;
        if (fromManufacturer) {
            return String(fromManufacturer);
        }
    }

    var aaiaBrandCode = getPartStringField(hit, 'aaia_brand_code');
    if (aaiaBrandCode) {
        return (
            'https://aam5.imgix.net/3/logos/manufacturer/' +
            aaiaBrandCode.toUpperCase() +
            '.png?fit=max&w=175&auto=compress,format'
        );
    }

    return undefined;
}

function getDerivedCategoryFields(hit, displayName) {
    var category = getPartStringField(hit, 'category');
    var subcategory = getPartStringField(hit, 'subcategory');

    if (subcategory && category) {
        return {
            name: subcategory,
            parent_slug: slugifyAutocompleteValue(category),
            slug: slugifyAutocompleteValue(subcategory),
        };
    }

    var name = displayName || category || subcategory || '';
    var catSlug = slugifyAutocompleteValue(category || name);

    return {
        name: name,
        parent_slug: catSlug,
        slug: subcategory
            ? slugifyAutocompleteValue(subcategory)
            : 'all-subcategories',
    };
}

/**
 * @param {object[]} partsHits
 * @param {'brand'|'category'|'series'} kind
 * @returns {object[]}
 */
export function deriveHitsFromParts(partsHits, kind) {
    if (!partsHits || !partsHits.length) {
        return [];
    }

    var seen = {};
    var out = [];

    partsHits.forEach(function (hit) {
        var fieldValues = [];

        if (kind === 'category') {
            var sub = getPartStringField(hit, 'subcategory');
            var cat = getPartStringField(hit, 'category');
            if (sub) {
                fieldValues.push(sub);
            } else if (cat) {
                fieldValues.push(cat);
            }
        } else {
            var value = getPartStringField(hit, kind);
            if (value) {
                fieldValues.push(value);
            }
        }

        fieldValues.forEach(function (name) {
            if (seen[name]) {
                return;
            }
            seen[name] = true;

            if (kind === 'category') {
                var categoryFields = getDerivedCategoryFields(hit, name);
                out.push({
                    objectID:
                        'derived:category:' +
                        categoryFields.parent_slug +
                        '/' +
                        categoryFields.slug,
                    name: categoryFields.name,
                    parent_slug: categoryFields.parent_slug,
                    slug: categoryFields.slug,
                    __derivedFromParts: true,
                });
                return;
            }

            if (kind === 'brand') {
                var brandSlug =
                    extractBrandSlugFromPart(hit) ||
                    slugifyAutocompleteValue(name);
                if (!brandSlug) {
                    return;
                }
                var image = getBrandImageFromPart(hit);
                var brandHit = {
                    objectID: 'derived:brand:' + brandSlug,
                    name: name,
                    slug: brandSlug,
                    __derivedFromParts: true,
                };
                if (image) {
                    brandHit.image = image;
                }
                out.push(brandHit);
                return;
            }

            // series — need a brand slug for browse URLs
            var seriesBrandSlug = extractBrandSlugFromPart(hit);
            if (!seriesBrandSlug) {
                return;
            }
            var seriesSlug = slugifyAutocompleteValue(name);
            if (!seriesSlug) {
                return;
            }
            out.push({
                objectID: 'derived:series:' + seriesSlug,
                name: name,
                slug: seriesSlug,
                brand_slug: seriesBrandSlug,
                __derivedFromParts: true,
            });
        });
    });

    return out;
}

export function resolveIndexKind(indexName) {
    var name = String(indexName || '').toLowerCase();
    if (/(^|_)parts$/.test(name)) {
        return 'parts';
    }
    if (/(^|_)brands$/.test(name)) {
        return 'brand';
    }
    if (/(^|_)categories$/.test(name)) {
        return 'category';
    }
    if (/(^|_)series$/.test(name)) {
        return 'series';
    }
    return null;
}

/**
 * Identity for a section row, shared by the merge below and the widgets'
 * transformItems so both agree on what counts as a duplicate.
 *
 * @param {'brand'|'category'|'series'} kind
 * @param {object} hit
 * @returns {string}
 */
export function autocompleteHitDedupeKey(kind, hit) {
    if (!hit) {
        return '';
    }

    var identity = hit.slug || hit.objectID || hit.id || hit.name || '';

    if (kind === 'category') {
        identity = (hit.parent_slug || '') + '|' + identity;
    } else if (kind === 'series') {
        identity = (hit.brand_slug || '') + '|' + identity;
    }

    return String(identity).trim().toLowerCase();
}

/**
 * {parentSlug|subcategory -> true} for the parents the matched parts actually
 * sit under. This is the only ground truth available for attributing a flat
 * facet bucket to one of several same-named category rows.
 *
 * @param {object[]} partsHits
 * @returns {object}
 */
function buildPartsCategoryOwnerPairs(partsHits) {
    var pairs = Object.create(null);

    (partsHits || []).forEach(function (hit) {
        var subcategory = getPartStringField(hit, 'subcategory');
        var category = getPartStringField(hit, 'category');
        if (!subcategory || !category) {
            return;
        }
        pairs[
            slugifyAutocompleteValue(category) +
                '|' +
                subcategory.trim().toLowerCase()
        ] = true;
    });

    return pairs;
}

/**
 * Names carried by more than one row under different parents. The taxonomy
 * allows the same subcategory label beneath two categories (get_categories()
 * keys children by chunk *within* a parent), and a flat `subcategory` facet
 * returns one bucket for both — so it cannot be split between them.
 *
 * @param {object[]} hits
 * @returns {object}
 */
function buildAmbiguousCategoryNames(hits) {
    var parentByName = Object.create(null);
    var ambiguous = Object.create(null);

    (hits || []).forEach(function (hit) {
        var name = String((hit && hit.name) || '').trim().toLowerCase();
        if (!name.length) {
            return;
        }
        var parent = String((hit && hit.parent_slug) || '')
            .trim()
            .toLowerCase();

        if (parentByName[name] === undefined) {
            parentByName[name] = parent;
            return;
        }
        if (parentByName[name] !== parent) {
            ambiguous[name] = true;
        }
    });

    return ambiguous;
}

/**
 * Count for one category row, or undefined when the facet cannot be attributed
 * to it. Shared by the ordering and the stamping below so the number a row is
 * ranked by is always the number it renders.
 *
 * @param {object|null} lookup
 * @param {object} ambiguous
 * @param {object} ownerPairs
 * @returns {function|null}
 */
function makeCategoryCountResolver(lookup, ambiguous, ownerPairs) {
    if (!lookup) {
        return null;
    }

    return function (hit) {
        var name = String((hit && hit.name) || '').trim().toLowerCase();
        if (!name.length) {
            return undefined;
        }

        var count = lookup[name];
        if (count === undefined) {
            return undefined;
        }
        if (!ambiguous[name]) {
            return count;
        }

        // One bucket, two parents: only the parent the matched parts confirm
        // may claim it. Neither claims it when the parts don't say.
        var parent = String((hit && hit.parent_slug) || '')
            .trim()
            .toLowerCase();
        return ownerPairs[parent + '|' + name] ? count : undefined;
    };
}

/**
 * Order rows by how many matching parts sit behind them, descending. Rows with
 * no count sink to the end — an absent count means nothing matched in them.
 *
 * Array#sort is stable, so equal counts and the uncounted tail keep the
 * relevancy order Meilisearch returned. Returns the input array untouched when
 * there is nothing to reorder, which lets the caller skip rebuilding.
 *
 * @param {object[]} hits
 * @param {function|null} countFor
 * @returns {object[]}
 */
function sortHitsByFacetCount(hits, countFor) {
    if (!countFor || !hits || hits.length < 2) {
        return hits;
    }

    function countOf(hit) {
        var count = countFor(hit);
        return count === undefined ? -1 : count;
    }

    return hits.slice().sort(function (a, b) {
        return countOf(b) - countOf(a);
    });
}

/**
 * Merge derived rows with a section's real hits, up to its hitsPerPage.
 * Derived rows lead — they describe the parts that actually matched, so a
 * part-number query surfaces that part's brand / category / series first and
 * the index's own fuzzy matches fall to whatever slots are left.
 *
 * @param {{ hits?: object[], hitsPerPage?: number, nbHits?: number }} result
 * @param {'brand'|'category'|'series'} kind
 * @param {object[]} partsHits
 */
function mergeDerivedHitsIntoResult(result, kind, partsHits, countFor) {
    var existingHits = result.hits || [];

    // 0 means "no limit came back from Meili" — take every row.
    var limit = parseInt(result.hitsPerPage, 10);
    if (isNaN(limit) || limit <= 0) {
        limit = 0;
    }

    var derived = deriveHitsFromParts(partsHits, kind);

    if (!derived.length && !countFor) {
        return;
    }

    var realByKey = {};
    existingHits.forEach(function (hit) {
        var key = autocompleteHitDedupeKey(kind, hit);
        if (key && !realByKey[key]) {
            realByKey[key] = hit;
        }
    });

    // When the index returned this same row, keep its copy — it carries fields
    // the derived one can't (category counts, canonical brand logos). Swapping
    // before ordering also means the two copies collapse to one candidate.
    var promotedDerived = derived.map(function (hit) {
        var key = autocompleteHitDedupeKey(kind, hit);
        return (key && realByKey[key]) || hit;
    });

    var sequence;
    if (!countFor) {
        // Nothing to rank by: derived lead, the index keeps Meili's order.
        sequence = promotedDerived.concat(existingHits);
    } else {
        // Only the first derived row is pinned. It names what the query
        // actually matched, and normalizeAutocompleteQuery's hyphen split lets
        // the index return unrelated rows carrying far bigger counts, so it has
        // to outrank them. Every row after it competes on matching-part count —
        // otherwise a query whose parts span five subcategories spends the whole
        // display budget on derived rows and a 900-match row never appears.
        sequence = promotedDerived
            .slice(0, 1)
            .concat(
                sortHitsByFacetCount(
                    promotedDerived.slice(1).concat(existingHits),
                    countFor
                )
            );
    }

    var seen = {};
    var merged = [];

    sequence.forEach(function (hit) {
        if (limit && merged.length >= limit) {
            return;
        }

        var key = autocompleteHitDedupeKey(kind, hit);
        if (key) {
            if (seen[key]) {
                return;
            }
            seen[key] = true;
        }

        merged.push(hit);
    });

    result.hits = merged;
    result.nbHits = Math.max(parseInt(result.nbHits, 10) || 0, merged.length);
    result.__derivedFromParts = true;
}

/**
 * Case-insensitive {facet value -> count} map, or null when the facet is
 * absent or entirely zero. Prototype-free so a subcategory named "constructor"
 * cannot collide with an inherited property.
 *
 * @param {object|undefined} facetDistribution
 * @param {string} facetName
 * @returns {object|null}
 */
export function buildPartsFacetCountLookup(facetDistribution, facetName) {
    var values =
        facetDistribution && typeof facetDistribution === 'object'
            ? facetDistribution[facetName]
            : null;

    if (!values || typeof values !== 'object') {
        return null;
    }

    var lookup = Object.create(null);
    var found = false;

    Object.keys(values).forEach(function (value) {
        var key = String(value).trim().toLowerCase();
        if (!key.length) {
            return;
        }
        var count = parseInt(values[value], 10);
        if (isNaN(count) || count <= 0) {
            return;
        }
        lookup[key] = count;
        found = true;
    });

    return found ? lookup : null;
}

/**
 * Stamp resolved counts onto rows. Only fills a gap — if the index ever starts
 * returning a real count field, extractAutocompleteCategoryCount() prefers it
 * over the `count` written here.
 *
 * @param {{ hits?: object[] }} result
 * @param {function|null} countFor
 */
function applyPartsFacetCounts(result, countFor) {
    if (!countFor || !result || !result.hits || !result.hits.length) {
        return;
    }

    result.hits.forEach(function (hit) {
        if (!hit || typeof hit !== 'object') {
            return;
        }
        if (hit.count !== undefined && hit.count !== null && hit.count !== '') {
            return;
        }
        var count = countFor(hit);
        if (count === undefined) {
            return;
        }
        hit.count = count;
    });
}

/**
 * Merge parts-derived rows into every brand / category / series result, and
 * attach parts-facet counts to the category rows.
 * Mutates `results` in place to match InstantSearch's multi-search shape.
 *
 * @param {Array<{ indexName?: string, params?: object|string }>} requests
 * @param {Array<{ hits?: object[], hitsPerPage?: number, nbHits?: number, facetDistribution?: object }>} results
 */
export function enrichAutocompleteResultsWithDerivedHits(requests, results) {
    if (!requests || !results || !results.length) {
        return results;
    }

    var partsHits = [];
    var partsFacetDistribution = null;
    for (var i = 0; i < requests.length; i++) {
        if (resolveIndexKind(requests[i].indexName) === 'parts') {
            partsHits = (results[i] && results[i].hits) || [];
            partsFacetDistribution =
                (results[i] && results[i].facetDistribution) || null;
            break;
        }
    }

    // No matching parts means no derived rows and no facet counts either.
    if (!partsHits.length) {
        return results;
    }

    var countLookup = buildPartsFacetCountLookup(
        partsFacetDistribution,
        PARTS_COUNT_FACET
    );
    var ownerPairs = buildPartsCategoryOwnerPairs(partsHits);

    for (var r = 0; r < requests.length; r++) {
        var kind = resolveIndexKind(requests[r].indexName);
        if (kind !== 'brand' && kind !== 'category' && kind !== 'series') {
            continue;
        }

        if (!results[r]) {
            continue;
        }

        // Only the category rows have counts, so only they can be ordered by
        // them; the other sections keep Meilisearch's own ranking. Ambiguity is
        // read off the index rows, before the merge folds derived rows in.
        var countFor =
            kind === 'category'
                ? makeCategoryCountResolver(
                      countLookup,
                      buildAmbiguousCategoryNames(results[r].hits),
                      ownerPairs
                  )
                : null;

        mergeDerivedHitsIntoResult(results[r], kind, partsHits, countFor);

        // Counts are independent of whether any derived row was produced, so
        // this runs after the merge and covers derived and index rows alike.
        if (kind === 'category') {
            applyPartsFacetCounts(results[r], countFor);
        }
    }

    return results;
}
